import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Star, Users, Check, Eye, EyeOff, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BACKEND_URL } from "../config";
import { formatHUF } from "../lib/format";
import Footer from "./components/Footer";
import { useFanClub } from "../contexts/AppContext";

export default function FanClubPage() {
  const navigate = useNavigate();
  const { member, tiers, login, register, createSubscriptionCheckout, completeSubscription, createUpgradeCheckout, logout, getMyAlbums } = useFanClub();
  const [activeTab, setActiveTab] = useState('login');
  const [upgradeLoading, setUpgradeLoading] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', tier_id: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [myAlbums, setMyAlbums] = useState([]);

  useEffect(() => {
    if (member?.api_token) {
      getMyAlbums().then((data) => setMyAlbums(data.albums || []));
    }
  }, [member?.id, member?.api_token]);

  useEffect(() => {
    if (tiers.length > 0 && !formData.tier_id) {
      setFormData(prev => ({ ...prev, tier_id: String(tiers[0].id) }));
    }
  }, [tiers]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const success = params.get('subscription_success');
    const cancelled = params.get('subscription_cancelled');

    if (cancelled === '1') {
      toast.info('A fizetés megszakítva. Bármikor újra próbálkozhatsz.');
      window.history.replaceState({}, '', '/fan-club');
      return;
    }

    if (success === '1' && sessionId) {
      const isUpgrade = params.get('upgrade') === '1';
      completeSubscription(sessionId)
        .then(() => {
          toast.success(isUpgrade ? 'Sikeres frissítés! Magasabb szinted aktív.' : 'Sikeres regisztráció és előfizetés! Üdv a Fan Clubban!');
          window.history.replaceState({}, '', '/fan-club');
        })
        .catch((err) => {
          toast.error(err.response?.data?.error || 'Hiba történt a regisztráció befejezésekor.');
          window.history.replaceState({}, '', '/fan-club');
        });
    }
  }, [completeSubscription]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Sikeres bejelentkezés!');
      navigate('/profilom', { replace: true });
    } catch (error) {
      const msg = error.response?.data?.errors?.email?.[0] || error.response?.data?.message || error.response?.data?.detail || 'Hiba történt';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await createSubscriptionCheckout(formData);
      if (res.is_free) {
        toast.success('Sikeres regisztráció!');
      } else if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.errors?.tier_id?.[0] || 'Hiba történt');
    } finally {
      setIsLoading(false);
    }
  };

  if (member) {
    return (
      <>
      <div className="min-h-screen pt-24 sm:pt-32 pb-32 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="fan-club-page">
        <div className="max-w-4xl mx-auto">
          <div className="glass rounded-2xl p-6 sm:p-8 md:p-12 animate-fade-in">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: member.tier?.color + '30' }}>
                <Crown className="h-8 w-8" style={{ color: member.tier?.color }} />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold font-['Syne']">Üdv, {member.name}!</h1>
                <p className="text-muted-foreground">{member.tier?.name}</p>
              </div>
              <Button variant="ghost" className="ml-auto" onClick={logout}>Kijelentkezés</Button>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-bold font-['Syne'] mb-4">Előnyeid</h2>
              <div className="grid gap-3">
                {member.tier?.benefits.split(',').map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <Check className="h-5 w-5 text-primary shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {myAlbums.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold font-['Syne'] mb-4">Vásárolt albumok – Letöltés</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {myAlbums.map((a) => (
                    <div key={a.album_id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      {a.cover_image && (
                        <img src={`${BACKEND_URL}/storage/${a.cover_image}`} alt={a.product_name} className="w-14 h-14 rounded-lg object-cover shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{a.product_name}</p>
                        <a href={a.download_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-primary text-sm hover:underline mt-1">
                          <Download className="h-4 w-4" /> Letöltés (ZIP)
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Csomagmódosítás – tagság bővítése */}
            {(() => {
              const currentTierId = member.tier?.id;
              const currentOrder = member.tier?.sort_order != null
                ? Number(member.tier.sort_order)
                : (tiers.find((t) => String(t.id) === String(currentTierId))?.sort_order ?? 0);
              const higherTiers = tiers
                .filter((t) => (Number(t.sort_order) || 0) > currentOrder)
                .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
              if (higherTiers.length === 0) return null;
              return (
              <div className="mb-8 p-6 bg-gradient-to-r from-primary/20 to-accent/20 rounded-xl">
                <h2 className="text-xl font-bold font-['Syne'] mb-2">Csomagmódosítás</h2>
                <p className="text-muted-foreground text-sm mb-4">Bővítsd a tagságodat: válts magasabb szintre több előnyért.</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {higherTiers
                    .map((tier) => (
                      <div key={tier.id} className="flex items-center justify-between gap-3 p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: (tier.color || '#888') + '30' }}>
                            <Crown className="h-5 w-5" style={{ color: tier.color }} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{tier.name}</p>
                            <p className="text-xs text-muted-foreground">{tier.price === 0 ? 'Ingyenes' : formatHUF(tier.price) + '/hó'}</p>
                          </div>
                        </div>
                        <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90 shrink-0" disabled={upgradeLoading === tier.id}
                          onClick={async () => {
                            setUpgradeLoading(tier.id);
                            try {
                              const res = await createUpgradeCheckout(tier.id);
                              if (res.is_free) {
                                toast.success('Frissítés sikeres!');
                              } else if (res.checkout_url) {
                                window.location.href = res.checkout_url;
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.message || err.response?.data?.errors?.tier_id?.[0] || 'Hiba történt');
                            } finally {
                              setUpgradeLoading(null);
                            }
                          }}>
                          {upgradeLoading === tier.id ? 'Feldolgozás...' : 'Bővítés'}
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
              );
            })()}
          </div>
        </div>
      </div>
      <Footer />
      </>
    );
  }

  return (
    <>
    <div className="min-h-screen pt-24 sm:pt-32 pb-32 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="fan-club-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 sm:mb-16 animate-fade-in text-center">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-primary mb-2 sm:mb-4">Csatlakozz</p>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold font-['Syne'] tracking-tighter mb-4">Fan Club</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">Légy része a közösségnek és élvezd az exkluzív előnyöket!</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
          {tiers.map((tier, index) => (
            <div key={tier.id} className={`glass rounded-xl p-4 sm:p-6 animate-fade-in relative ${formData.tier_id === tier.id ? 'ring-2' : ''}`}
              style={{ animationDelay: `${index * 0.1}s`, borderColor: tier.color, ringColor: tier.color }}>
              {tier.id === 'tier-gold' && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-primary">Népszerű</Badge></div>}
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: tier.color + '30' }}>
                {tier.id === 'tier-free' && <Users className="h-6 w-6" style={{ color: tier.color }} />}
                {tier.id === 'tier-silver' && <Star className="h-6 w-6" style={{ color: tier.color }} />}
                {tier.id === 'tier-gold' && <Star className="h-6 w-6" style={{ color: tier.color }} />}
                {tier.id === 'tier-vip' && <Crown className="h-6 w-6" style={{ color: tier.color }} />}
              </div>
              <h3 className="text-lg font-bold font-['Syne'] mb-1">{tier.name}</h3>
              <p className="text-2xl font-bold mb-4" style={{ color: tier.color }}>{tier.price === 0 ? 'Ingyenes' : `${formatHUF(tier.price)}/hó`}</p>
              <ul className="space-y-2 mb-6">
                {(() => {
                  const benefitList = tier.benefits.split(',').map(b => b.trim()).filter(Boolean);
                  return (
                    <>
                      {benefitList.slice(0, 4).map((benefit, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: tier.color }} />
                          <span>{benefit}</span>
                        </li>
                      ))}
                      {benefitList.length > 4 && <li className="text-sm text-muted-foreground">+{benefitList.length - 4} további előny</li>}
                    </>
                  );
                })()}
              </ul>
              <Button className="w-full rounded-full" style={{ backgroundColor: tier.color }} onClick={() => { setFormData(prev => ({ ...prev, tier_id: tier.id })); setActiveTab('register'); }}>
                Választom
              </Button>
            </div>
          ))}
        </div>

        <div className="max-w-md mx-auto glass rounded-2xl p-6 sm:p-8 animate-fade-in">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Bejelentkezés</TabsTrigger>
              <TabsTrigger value="register">Regisztráció</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Email</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required className="bg-black/40 border-white/10 h-12 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Jelszó</label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} required className="bg-black/40 border-white/10 h-12 rounded-lg pr-10" />
                    <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1 h-10 w-10" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <Button type="submit" disabled={isLoading} className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-bold">
                  {isLoading ? 'Bejelentkezés...' : 'Bejelentkezés'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Név</label>
                  <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required className="bg-black/40 border-white/10 h-12 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Email</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required className="bg-black/40 border-white/10 h-12 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Jelszó</label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} required className="bg-black/40 border-white/10 h-12 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Tagság</label>
                  <select value={formData.tier_id} onChange={(e) => setFormData(prev => ({ ...prev, tier_id: e.target.value }))} className="w-full bg-black/40 border border-white/10 h-12 rounded-lg px-4" required>
                    {tiers.map(t => <option key={t.id} value={t.id}>{t.name} - {t.price === 0 ? 'Ingyenes' : `${formatHUF(t.price)}/hó (havi ismétlődő)`}</option>)}
                  </select>
                </div>
                <Button type="submit" disabled={isLoading || !formData.tier_id} className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-bold">
                  {isLoading ? 'Regisztráció...' : (tiers.find(t => t.id == formData.tier_id)?.price > 0 ? 'Regisztráció és fizetés →' : 'Regisztráció')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
    <Footer />
    </>
  );
}
